import { styled } from '@mui/material/styles';
import { COLOR } from '@src/styles/color';

// Card and gap come straight from the design. One click on an arrow advances by
// exactly one card, so a card edge always lines up with the fade at the edge of
// the strip instead of stopping halfway through a card.
const CARD_WIDTH = 320;
const CARD_GAP = 24;
const ARTWORK_HEIGHT = 200;

export const SCROLL_STEP = CARD_WIDTH + CARD_GAP;

export const StyledEducatorsHomeFeatures = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  box-sizing: border-box;

  .features-header {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  /* glowing-star.svg wraps a bitmap, so it cannot be recoloured and needs an
     explicit size — it is treated as a raster icon, not as a glyph. */
  .features-header-icon {
    display: flex;
    align-items: center;
    padding: 4px;

    img {
      display: block;
      width: 24px;
      height: 24px;
    }
  }

  /* The fade and its arrow sit on top of the row, so the row owns the
     positioning context. */
  .features-strip {
    position: relative;
  }

  .features-scroll {
    display: flex;
    gap: ${CARD_GAP}px;
    /* Leaves the last card clear of the edge once the row is scrolled out. */
    padding-right: 60px;
    overflow-x: auto;
    /* The design closes the row with a fade instead of a scrollbar. */
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  /* Spans the whole card, titles included: the design centres the arrow on the
     card, not on the artwork. Stretching to the row instead of naming a height
     keeps that true when a description wraps to another line. */
  .features-edge {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 86px;
    display: flex;
    align-items: center;
    /* The fade takes the clicks on purpose. The card behind it is only half
       visible here, and its add button covers the whole artwork, so a click
       landing on it is an accident rather than an intent. */
    pointer-events: auto;
    z-index: 2;

    &.left {
      left: 0;
      justify-content: flex-start;
      background: linear-gradient(
        to right,
        ${COLOR.white} 40%,
        rgba(255, 255, 255, 0)
      );
    }

    &.right {
      right: 0;
      justify-content: flex-end;
      background: linear-gradient(
        to left,
        ${COLOR.white} 40%,
        rgba(255, 255, 255, 0)
      );
    }
  }

  .features-arrow {
    width: 40px;
    height: 40px;
    padding: 8px;
    background-color: ${COLOR.white};
    border: 0.5px solid ${COLOR.indigo[16]};
    border-radius: 50%;
    box-shadow: 0 0 8px rgba(0, 0, 0, 0.25);
    -webkit-backdrop-filter: blur(2px);
    backdrop-filter: blur(2px);

    &:hover {
      background-color: ${COLOR.white};
    }

    .features-arrow-icon {
      width: 24px;
      height: 24px;
      color: ${COLOR.indigo[64]};
    }
  }

  .feature-card {
    width: ${CARD_WIDTH}px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .feature-artwork {
    position: relative;
    height: ${ARTWORK_HEIGHT}px;
    border-radius: 16px;
    border: 0.5px solid ${COLOR.indigo[16]};
    background-color: ${COLOR.charcoalV2[64]};
    overflow: hidden;
    box-sizing: border-box;
  }

  .feature-image {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  /* Hover state, from the "Trạng thái hover" frame: the artwork blurs behind a
     white wash and the add button comes forward. Fading it in and out keeps
     entering and leaving the card smooth. */
  .feature-hover {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    /* Repeats the card radius: a backdrop-filtered layer is not reliably
       clipped by the rounded parent in Chrome. */
    border-radius: 16px;
    background-color: rgba(255, 255, 255, 0.16);
    -webkit-backdrop-filter: blur(4px);
    backdrop-filter: blur(4px);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease;
  }

  .feature-add,
  .feature-pin {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    border-radius: 8px;
    cursor: pointer;
  }

  .feature-add {
    width: 40px;
    height: 40px;
    background-color: ${COLOR.indigo.default};

    .feature-add-icon {
      width: 20px;
      height: 20px;
      color: ${COLOR.white};
    }

    /* Hovering the artwork already means the user is going for this button, so
       the whole image counts as a click on it — aiming at the 40px pill is not
       required. The button is left unpositioned on purpose: this layer then
       resolves against .feature-artwork, so it covers the image rather than
       just the pill. Stretching the real button beats a click handler on the
       artwork — the popover stays anchored to the pill, and the pin (a later
       sibling of .feature-hover, so painted above) keeps its own click. */
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: 16px;
    }
  }

  /* The pin sits outside .feature-hover: a pinned card keeps showing it after
     the pointer leaves, the way the pinned card in the design does. Being last
     in the markup also keeps it clear of the blur behind it. */
  .feature-pin {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 32px;
    height: 32px;
    background-color: ${COLOR.white};
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease;

    .feature-pin-icon {
      width: 20px;
      height: 20px;
      color: ${COLOR.indigo[80]};
    }
  }

  /* Hovering only brings the blur layer forward — the card keeps its resting
     border and background. */
  .feature-card:hover {
    .feature-hover,
    .feature-pin {
      opacity: 1;
      pointer-events: auto;
    }
  }

  /* While a card's menu is open the pointer sits on the popover, not on the
     card, so the hover state has to be held open explicitly — otherwise the
     add button the menu is anchored to fades out from under it. */
  .feature-card.active {
    .feature-hover,
    .feature-pin {
      opacity: 1;
      pointer-events: auto;
    }
  }

  .feature-card.pinned .feature-pin {
    opacity: 1;
    pointer-events: auto;
  }

  .feature-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 0 4px;
    box-sizing: border-box;
  }
`;

/* The menu is rendered through a portal, so it sits outside the section above
   and needs its own styled root. Sizes match the same menu on the projects
   page (Project/ProjectInput/index.style.js). */
export const StyledCreateAudioMenu = styled('div')`
  background-color: ${COLOR.white};
  border-radius: 8px;
  padding: 8px;
  width: 224px;
  box-sizing: border-box;

  .create-audio-item {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    width: 100%;
    padding: 8px;
    border: none;
    background: none;
    text-align: left;
    cursor: pointer;
    border-radius: 8px;
    box-sizing: border-box;

    &:hover {
      background-color: ${COLOR.backgroundRecordTime};
    }
  }
`;
